import { Bell } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import type { CanvaschoolApi } from "../api/client";
import type { NotificationList } from "@canvaschool/contracts";

const SENT_AT = new Intl.DateTimeFormat("ko-KR", {
  month: "long",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Asia/Seoul",
});

/**
 * 헤더의 알림 종. 지금은 내 의견·요청에 운영팀 답변이 달린 것만 알린다.
 * 실시간이 아니다: 화면을 열거나 옮길 때(`refreshKey`가 바뀔 때) 다시 읽는다.
 * 패널을 열면 보이는 안 읽은 알림을 읽음으로 돌린다.
 */
export const NotificationBell = ({
  api,
  refreshKey,
}: {
  api: Pick<CanvaschoolApi, "listNotifications" | "markNotificationsRead">;
  refreshKey: string;
}) => {
  const [list, setList] = useState<NotificationList | null>(null);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    let current = true;
    api.listNotifications().then(
      (next) => current && setList(next),
      // The bell is a convenience; a failed read just leaves it as it was.
      () => {},
    );
    return () => {
      current = false;
    };
  }, [api, refreshKey]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointer = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = () => {
    const opening = !open;
    setOpen(opening);
    const unread = list?.items.filter((item) => item.readAt === null) ?? [];
    if (!opening || unread.length === 0) {
      return;
    }
    api.markNotificationsRead(unread.map((item) => item.id)).then(
      () => {
        const readAt = new Date().toISOString();
        setList(
          (current) =>
            current && {
              unreadCount: 0,
              items: current.items.map((item) =>
                item.readAt === null ? { ...item, readAt } : item,
              ),
            },
        );
      },
      () => {},
    );
  };

  const unreadCount = list?.unreadCount ?? 0;
  return (
    <div className="canvaschool-notifications" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="canvaschool-notifications-button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={unreadCount > 0 ? `알림, 새 알림 ${unreadCount}개` : "알림"}
        onClick={toggle}
      >
        <Bell size={18} aria-hidden="true" />
        {unreadCount > 0 && (
          <span className="canvaschool-notifications-badge" aria-hidden="true">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div
          id={panelId}
          className="canvaschool-notifications-panel"
          role="region"
          aria-label="알림"
        >
          {!list || list.items.length === 0 ? (
            <p className="canvaschool-notifications-empty">새 알림이 없어요.</p>
          ) : (
            <ol>
              {list.items.map((item) => (
                <li
                  key={item.id}
                  className={
                    item.readAt === null
                      ? "canvaschool-notifications-item--unread"
                      : undefined
                  }
                >
                  <p className="canvaschool-notifications-title">
                    보낸 의견·요청에 답변이 달렸어요
                  </p>
                  <time dateTime={item.report.repliedAt}>
                    {SENT_AT.format(new Date(item.report.repliedAt))}
                  </time>
                  <blockquote>{item.report.message}</blockquote>
                  <p className="canvaschool-notifications-reply">
                    {item.report.reply}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
};
